import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, RefreshCw, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const TOKEN_KEY = "ferrer_admin_token";

const STATUSES = ["new", "contacted", "quoted", "booked", "closed"];

const Admin = () => {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(TOKEN_KEY);
    if (saved) {
      verify(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = async (t) => {
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "x-admin-token": t } });
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      setAuthed(true);
      load(t);
    } catch {
      toast.error("Invalid admin token");
      localStorage.removeItem(TOKEN_KEY);
    }
  };

  const load = async (t = token) => {
    setLoading(true);
    try {
      const [q, c] = await Promise.all([
        axios.get(`${API}/quotes`, { headers: { "x-admin-token": t } }),
        axios.get(`${API}/contact`, { headers: { "x-admin-token": t } }),
      ]);
      setQuotes(q.data);
      setContacts(c.data);
    } catch {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/quotes/${id}/status`, null, {
        params: { status },
        headers: { "x-admin-token": token },
      });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success("Status updated");
    } catch {
      toast.error("Could not update status");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this quote?")) return;
    try {
      await axios.delete(`${API}/quotes/${id}`, { headers: { "x-admin-token": token } });
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
    setToken("");
    setQuotes([]);
    setContacts([]);
  };

  if (!authed) {
    return (
      <div data-testid="admin-login" className="min-h-screen bg-[#050914] text-white flex items-center justify-center px-6">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            verify(token);
          }}
          className="border border-white/10 bg-[#0B132B]/40 p-10 w-full max-w-md"
        >
          <div className="w-12 h-12 border border-white/15 flex items-center justify-center mb-6">
            <Lock className="w-5 h-5" />
          </div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400 mb-3">Admin Access</div>
          <h1 className="font-display text-3xl font-semibold mb-8">Ferrer Console</h1>
          <label className="text-[11px] uppercase tracking-[0.25em] text-slate-400 block mb-2">Admin Token</label>
          <input
            data-testid="admin-token-input"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            className="w-full bg-transparent border-b border-white/20 focus:border-white text-white py-3 outline-none"
            required
          />
          <button
            type="submit"
            data-testid="admin-login-submit"
            className="mt-8 w-full bg-white text-[#050914] py-3 text-[12px] uppercase tracking-[0.25em] font-semibold hover:bg-slate-200"
          >
            Sign In
          </button>
        </form>
      </div>
    );
  }

  const rows = tab === "quotes" ? quotes : contacts;

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#050914] text-white pt-28 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-12">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400 mb-2">Console</div>
            <h1 className="font-display font-bold text-4xl md:text-5xl">Submissions</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => load()}
              data-testid="admin-refresh"
              className="border border-white/15 px-5 py-2.5 text-[11px] uppercase tracking-[0.25em] hover:bg-white hover:text-[#050914] flex items-center gap-2"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="text-[11px] uppercase tracking-[0.25em] text-slate-400 hover:text-white"
            >
              Sign out
            </button>
          </div>
        </div>

        <div className="flex gap-6 border-b border-white/10 mb-8">
          {["quotes", "contacts"].map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`pb-4 text-[12px] uppercase tracking-[0.25em] border-b-2 -mb-px ${
                tab === t ? "border-white text-white" : "border-transparent text-slate-400 hover:text-white"
              }`}
            >
              {t} ({t === "quotes" ? quotes.length : contacts.length})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-slate-400 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="border border-white/10 bg-[#0B132B]/40 p-16 text-center">
            <div className="text-slate-400">No {tab} yet.</div>
          </div>
        ) : tab === "quotes" ? (
          <div className="border border-white/10 bg-[#0B132B]/30 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-white/10">
                <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-slate-400">
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4">Name</th>
                  <th className="px-5 py-4">Contact</th>
                  <th className="px-5 py-4">Vehicle</th>
                  <th className="px-5 py-4">Route</th>
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((q) => (
                  <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-b border-white/5 hover:bg-white/5">
                    <td className="px-5 py-4 text-slate-400 whitespace-nowrap">
                      {new Date(q.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-4 text-white">{q.name}</td>
                    <td className="px-5 py-4 text-slate-300">
                      <div>{q.email}</div>
                      <div className="text-slate-500 text-xs">{q.phone}</div>
                    </td>
                    <td className="px-5 py-4 text-slate-300">{q.vehicle_type}</td>
                    <td className="px-5 py-4 text-slate-300 whitespace-nowrap">
                      {q.pickup_state} → {q.delivery_state}
                    </td>
                    <td className="px-5 py-4 text-slate-400">{q.preferred_date || "—"}</td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`quote-status-${q.id}`}
                        value={q.status}
                        onChange={(e) => updateStatus(q.id, e.target.value)}
                        className="bg-[#050914] border border-white/15 text-white text-xs px-2 py-1.5 uppercase tracking-wider"
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() => remove(q.id)}
                        data-testid={`quote-delete-${q.id}`}
                        className="text-slate-500 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="border border-white/10 bg-[#0B132B]/30 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-white/10">
                <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-slate-400">
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4">Name</th>
                  <th className="px-5 py-4">Email</th>
                  <th className="px-5 py-4">Phone</th>
                  <th className="px-5 py-4">Message</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id} className="border-b border-white/5">
                    <td className="px-5 py-4 text-slate-400 whitespace-nowrap">
                      {new Date(c.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-4 text-white">{c.name}</td>
                    <td className="px-5 py-4 text-slate-300">{c.email}</td>
                    <td className="px-5 py-4 text-slate-300">{c.phone || "—"}</td>
                    <td className="px-5 py-4 text-slate-300 max-w-md">{c.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
